import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { PublicLayout } from "@/components/Navbar";
import { StockBadge } from "@/components/StockBadge";
import { Search, Minus, Plus, ShoppingCart, Bell } from "lucide-react";

export default function RetailerCatalog() {
  const { refreshCart } = useCart();
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ categories: [] });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [qty, setQty] = useState({});

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (category) params.category = category;
    api.get("/retailer/products", { params }).then(({ data }) => {
      setProducts(data);
      setQty((q) => { const n = { ...q }; data.forEach((p) => { if (!n[p.sku]) n[p.sku] = p.min_order_quantity; }); return n; });
    });
  };
  useEffect(() => { api.get("/public/meta").then(({ data }) => setMeta(data)); }, []);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, category]);

  const setq = (sku, v, min) => setQty((q) => ({ ...q, [sku]: Math.max(min, (v || min)) }));

  const addToCart = async (p) => {
    try {
      await api.post("/retailer/cart", { sku: p.sku, quantity: qty[p.sku] || p.min_order_quantity });
      await refreshCart();
      toast.success(`${qty[p.sku]} × ${p.name} added to cart`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const watch = async (p) => {
    try {
      await api.post("/retailer/watchlist", { sku: p.sku, quantity: 1 });
      toast.success(`We'll alert you when ${p.name} is back in stock`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">Trade Ordering Portal</span>
            <h1 className="mt-2 font-serif-display text-4xl font-bold text-slate-50">Order with live stock</h1>
            <p className="mt-2 text-sm text-[#94A3B8]">Prices reflect your account tier. Availability is verified against Sage 50 at checkout.</p>
          </div>
          <Link to="/portal/orders" data-testid="goto-orders-btn" className="rounded-full border border-[#2A2E39] px-5 py-2.5 text-sm font-semibold text-slate-200 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">My Orders</Link>
        </div>

        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748B]" />
            <input data-testid="retailer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…"
              className="w-full rounded-full border border-[#2A2E39] bg-[#1A1D24] py-3 pl-11 pr-4 text-sm text-slate-100 placeholder:text-[#64748B] focus:border-[#D4AF37]/60 focus:outline-none" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCategory("")} className={`rounded-full border px-4 py-2 text-sm ${category === "" ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2E39] text-slate-300"}`}>All</button>
            {meta.categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)} className={`rounded-full border px-4 py-2 text-sm ${category === c ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2E39] text-slate-300"}`}>{c}</button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((p) => {
            const out = p.stock_status === "OUT OF STOCK";
            return (
              <div key={p.sku} data-testid={`retailer-product-${p.sku}`} className="mc-card flex flex-col overflow-hidden rounded-2xl">
                <div className="relative aspect-square overflow-hidden bg-white p-4">
                  <img src={p.image_url} alt={p.name} className="h-full w-full object-contain" />
                  <div className="absolute left-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#D4AF37] backdrop-blur">{p.brand}</div>
                  <div className="absolute right-3 top-3"><StockBadge status={p.stock_status} testId={`retailer-stock-${p.sku}`} /></div>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-serif-display text-lg font-semibold text-slate-100">{p.name}</h3>
                  <div className="mt-1 flex flex-wrap gap-x-2 text-xs text-[#94A3B8] font-mono-data">{p.sku} • {p.pack_size} • {p.units_per_case}/case</div>

                  <div className="mt-3 flex items-end justify-between">
                    <div>
                      <div data-testid={`retailer-price-${p.sku}`} className="text-2xl font-bold text-[#D4AF37]">€{p.trade_price.toFixed(2)}</div>
                      <div className="text-[11px] text-[#64748B]">+{p.vat_rate}% VAT • MOQ {p.min_order_quantity}</div>
                    </div>
                    {typeof p.available_quantity === "number" && (
                      <div className="text-right text-[11px] text-[#94A3B8]">{p.available_quantity} avail.</div>
                    )}
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <div className="flex items-center rounded-full border border-[#2A2E39]">
                      <button data-testid={`qty-dec-${p.sku}`} onClick={() => setq(p.sku, (qty[p.sku] || p.min_order_quantity) - 1, p.min_order_quantity)} className="p-2.5 text-slate-300 hover:text-[#D4AF37]"><Minus className="h-3.5 w-3.5" /></button>
                      <input data-testid={`qty-input-${p.sku}`} value={qty[p.sku] || p.min_order_quantity} onChange={(e) => setq(p.sku, parseInt(e.target.value) || p.min_order_quantity, p.min_order_quantity)}
                        className="w-12 bg-transparent text-center text-sm font-semibold text-slate-100 focus:outline-none" />
                      <button data-testid={`qty-inc-${p.sku}`} onClick={() => setq(p.sku, (qty[p.sku] || p.min_order_quantity) + 1, p.min_order_quantity)} className="p-2.5 text-slate-300 hover:text-[#D4AF37]"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    {out ? (
                      <button data-testid={`notify-${p.sku}`} onClick={() => watch(p)}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[#D4AF37]/40 px-3 py-2.5 text-sm font-semibold text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10">
                        <Bell className="h-4 w-4" /> Notify me
                      </button>
                    ) : (
                      <button data-testid={`add-to-cart-${p.sku}`} onClick={() => addToCart(p)}
                        className="mc-btn-gold inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-2.5 text-sm">
                        <ShoppingCart className="h-4 w-4" /> Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </PublicLayout>
  );
}
